import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import type { SessionTokens } from '@/integrations/Immersve/CalciteClient';
import { KycSessionStorage } from '@/common/KycSessionStorage';
import * as Sentry from '@sentry/vue';
import { extractImmersveAuthSentryData } from '@/util/sentry/eventProcessing';
import { CalciteClient } from '@/integrations/Immersve/CalciteClient';
import { jwtDecode } from 'jwt-decode';
import { config } from '@/config';
import type { StepSet } from '@/models/UserFlow';

export interface KycSessionData {
  auth?: {
    accessToken?: string;
    refreshToken?: string;
  };
  externalSession?: {
    token: string;
  };
  redirectUrl?: string;
  initialStepSet?: StepSet;
  consentsGiven?: string[];
}

function trySetSentryTagsFromAccessToken(accessToken: string | undefined) {
  if (!accessToken) {
    return;
  }

  const { cardholderAccountId, sid } = extractImmersveAuthSentryData(accessToken);
  if (cardholderAccountId) {
    Sentry.setTag('cardholderAccountId', cardholderAccountId);
  }
  if (sid) {
    Sentry.setTag('sessionId', sid);
  }
}

export const useImmersveAuthStore = defineStore('AuthStore', () => {
  const accessToken = ref<string | undefined>(undefined);
  const refreshToken = ref<string | undefined>(undefined);
  const currentSessionId = ref<string | undefined>(undefined);
  const tokenRefreshTimeout = ref<NodeJS.Timeout | undefined>(undefined);

  function initialiseSession(sessionId: string) {
    currentSessionId.value = sessionId;
    const data = KycSessionStorage.getDataForSession<KycSessionData>(currentSessionId.value);

    if (data.auth) {
      accessToken.value = data.auth?.accessToken;
      refreshToken.value = data.auth?.refreshToken;
    }
  }

  function setSessionTokens(tokens: SessionTokens) {
    if (!currentSessionId.value) {
      throw new Error('Session Id must be set');
    }
    accessToken.value = tokens.accessToken;
    refreshToken.value = tokens.refreshToken;

    KycSessionStorage.updateDataForSession<KycSessionData>(currentSessionId.value, {
      auth: {
        ...tokens,
      },
    });
  }

  function expiresInMs(token: string): number {
    if (!token) return 0;
    const exp: number | undefined = jwtDecode<{ exp: number }>(token).exp;
    return exp ? exp * 1000 - Date.now() : 0;
  }

  function isTokenExpired(token: string): boolean {
    return expiresInMs(token) <= config.tokenExpiryBufferMs;
  }

  function hasInvalidAccessToken(): boolean {
    return !accessToken.value || isTokenExpired(accessToken.value);
  }

  function hasInvalidRefreshToken(): boolean {
    return !refreshToken.value || isTokenExpired(refreshToken.value);
  }

  async function obtainTokens(authCode: string) {
    const calciteClient = new CalciteClient();
    const tokens = await calciteClient.redeemAuthCode(authCode);
    setSessionTokens(tokens);
  }

  async function refreshTokens() {
    if (!refreshToken.value) {
      throw new Error('Refresh token is not available');
    }
    if (isTokenExpired(refreshToken.value)) {
      throw new Error('Refresh token has expired');
    }
    const calciteClient = new CalciteClient();
    const tokens = await calciteClient.redeemRefreshToken({ refreshToken: refreshToken.value });
    setSessionTokens(tokens);
  }

  async function scheduleTokenRefresh() {
    if (!accessToken.value || !refreshToken.value) return;

    const needsRefreshInMs = expiresInMs(accessToken.value) - config.tokenExpiryBufferMs;

    if (needsRefreshInMs <= 0) {
      await refreshTokens();
    } else {
      if (tokenRefreshTimeout.value) {
        clearTimeout(tokenRefreshTimeout.value);
      }
      tokenRefreshTimeout.value = setTimeout(scheduleTokenRefresh, needsRefreshInMs);
    }
  }

  watch(
    () => accessToken.value,
    (newToken, oldToken) => {
      // if the new access token is invalid (e.g. expired token loaded from storage), refreshing it will be handled by beforeResolveGuard
      // and then the updated token will be scheduled for refresh
      if (newToken !== oldToken && !hasInvalidAccessToken()) {
        trySetSentryTagsFromAccessToken(newToken);
        scheduleTokenRefresh();
      }
    }
  );

  return {
    accessToken,
    refreshToken,
    sessionId: currentSessionId,
    initialiseSession,
    setSessionTokens,
    hasInvalidAccessToken,
    hasInvalidRefreshToken,
    obtainTokens,
    refreshTokens,
    isTokenExpired,
    scheduleTokenRefresh,
  };
});
